import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableAlertsStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportAlertsLocation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchAlertsReportStation,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setAlertsStationReportUpdate,
} from '../../../../redux/slices/ReportAlertsLocationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
  rightTopContent?: React.ReactElement;
}

/**
 * Reports management page
 * @param {any} properties - The properties
 */
function AlertsReportTableStation(properties: Readonly<ReportTableProperties>) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter, rightTopContent } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isAlertsStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportAlertsLocation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);

  const { handleOpenStationSidePanel } = useTableSidePanel({ sessions: reports });

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableAlertsStationReport,
      /**
       * S.No view
       * @param {any} report - The report
       * @param {any} index - The index
       */
      customData: (report: AvailableAlertsStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'stationInfo' as keyof AvailableAlertsStationReport,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableAlertsStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span
              role="presentation"
              className="hyperlink"
              onClick={(event) => { event.stopPropagation(); handleOpenStationSidePanel(report); }}
            >
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'createdAt',
      queryParamsValue: 'createdAt',
      sidePanelComponent: <LocationSidePanel fetchIdFrom="queryParams" />,
      sortAllow: true,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('basePricingAlertsConsumption'),
      accessorKey: 'basePricePowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },

    {
      label: t('peakPricingAlertsConsumption'),
      accessorKey: 'peakPricePowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('offPeakPricingAlertsConsumption'),
      accessorKey: 'offPeakPricePowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
    {
      label: t('scheduleChargeAlertsConsumption'),
      accessorKey: 'scheduledPowerConsumed' as keyof AvailableAlertsStationReport,
      sortAllow: true,
    },
  ];
  /**
   * Fetches Alerts report data for the selected facility.
   * @param {any} data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchAlertsReportStation({
          ...data,
        }),
      );
    }
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
      accountCustomId: reportsData?.accountId,
    });
  };

  /**
   * Current columns for Alert Station Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('stationId'), accessorKey: 'stationId' },
    { label: t('stationName'), accessorKey: 'stationName' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumed' },
    { label: t('revenueGenerated'), accessorKey: 'revenueGenerated' },
  ];

  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchAlertStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: AvailableAlertsStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row?.createdAt ? ConvertUTCtoDate(row.createdAt) : '-';

              // case 'stationId':
              //   return row?.stationId || '-';

              // case 'stationName':
              //   return row?.stationName || '-';

              // case 'powerConsumed':
              //   return row?.powerConsumed ?? '0';

              // case 'revenueGenerated':
              //   return row?.revenueGenerated ?? '0';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  // Add this new function (placed near handleLimitChange or handlePageChange)
  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      accountCustomId: reportsData?.accountId,
    });
  };

  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
      accountCustomId: reportsData?.accountId,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isAlertsStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setAlertsStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isAlertsStationReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'alertsReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter: any) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show: any) => single?.value?.includes(show.value))
              .map((show: any) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
        accountCustomId: reportsData?.accountId,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          sizeChangeButtonRequired
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={handleApiRequest}
          version="v2"
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          height={370}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          refershCurrentPages={ReferenceCurrentPages}
          onHiddenColumnsChange={setHiddenColumns}
          rightTopContent={rightTopContent}
        />
      </div>
    </div>
  );
}

export default AlertsReportTableStation;

